import { API_BASE_URL } from '../constant/constant';

// Helper function to handle API responses
const handleResponse = async (response) => {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
  }
  return response.json();
};

/**
 * Submit feedback for a translation
 * @param {Object} feedbackData - The feedback data
 * @param {string} feedbackData.translationId - The ID of the translation
 * @param {string} feedbackData.type - 'positive' or 'negative'
 * @param {string} feedbackData.input - The input text that was translated
 * @param {string} feedbackData.output - The translated output text
 * @param {string} feedbackData.fromLanguage - The source language
 * @param {string} feedbackData.toLanguage - The target language
 * @param {string} feedbackData.api_endpoint - The API endpoint used for translation
 * @param {string} feedbackData.feedback_text - The feedback text/comment
 * @param {boolean} feedbackData.is_liked - Whether the translation was liked (true for positive, false for negative)
 * @param {string} [feedbackData.issue] - Issue type (for negative feedback)
 * @param {string} [feedbackData.comment] - Additional comment
 * @param {string} [feedbackData.timestamp] - Timestamp of feedback
 * @param {Object} [userInfo] - Optional user information
 * @param {string} [userInfo.user_id] - User ID if authenticated
 * @param {string} [userInfo.email] - User email
 * @param {string} [userInfo.full_name] - User full name
 * @returns {Promise<Object>} The API response
 */
export const submitFeedback = async (feedbackData, userInfo = {}) => {
  try {
    const requestBody = {
      platform: 'Web',
      input: feedbackData.input,
      output: feedbackData.output,
      fromLanguage: feedbackData.fromLanguage,
      toLanguage: feedbackData.toLanguage,
      feedback_text: feedbackData.feedback_text,
      is_liked: feedbackData.is_liked,
      translation_id: feedbackData.translationId,
      feedback_type: feedbackData.type,
      ...(feedbackData.issue && { issue: feedbackData.issue }),
      ...(feedbackData.comment && { comment: feedbackData.comment }),
      ...(feedbackData.timestamp && { timestamp: feedbackData.timestamp }),
      // Add user information if provided
      ...(userInfo.user_id && { user_id: userInfo.user_id }),
      ...(userInfo.email && { email: userInfo.email }),
      ...(userInfo.full_name && { full_name: userInfo.full_name }),
    };

    const response = await fetch(`${API_BASE_URL}/feedback`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    return await handleResponse(response);
  } catch (error) {
    console.error('Error submitting feedback:', error);
    throw error;
  }
};

